import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Route, Routes, useNavigate} from 'react-router-dom';
import HeaderComponent from './components/HeaderComponent/HeaderComponent';
import './App.css';
import AddMerchantAccountComponent from "./components/AddMerchantAccountComponent/AddMerchantAccountComponent";
import EditMerchantAccountComponent from "./components/EditMerchantAccountComponent/EditMerchantAccountComponent";
import DeleteMerchantAccountComponent from "./components/DeleteMerchantAccountComponent/DeleteMerchantAccountComponent";
import MerchantListComponent from "./components/MerchantListComponent/MerchantListComponent";
import MerchantConfigurationsListComponent from "./components/MerchantConfigurationsListComponent/MerchantConfigurationsListComponent";
import ErrorComponent from "./components/ErrorComponent/ErrorComponent";
import LoginComponent from "./components/LoginComponent/LoginComponent";
import ResetPasswordComponent from "./components/ResetPasswordComponent/ResetPasswordComponent";
import PrivateRoute from "./utils/PrivateRoute";
import {AuthProvider} from "./utils/AuthContext";
import SetPasswordComponent from "./components/SetPasswordComponent/SetPasswordComponent";
import SessionMonitorComponent from './components/SessionMonitorComponent/SessionMonitorComponent';
import PaymentAdviceListComponent from "./components/PaymentAdviceListComponent/PaymentAdviceListComponent";
import {fetchMerchants, MerchantResponse} from "./api/MerchantsApi";
import {PAYMENT_ADVICE_ENABLED, TRANSACTIONS_ENABLED} from "./AppConst";
import {TransactionsComponent} from "./components/TransactionListComponent/TransactionsComponent";
import {testAdyenConnection} from "./api/CheckConnectionApi";
import ChangeEmailComponent from "./components/ChangeEmailComponent/ChangeEmailComponent";
import ConnectionStatusPopup from "./components/ConnectionStatusPopup/ConnectionStatusPopup";

const App: React.FC = () => {
    const [mainHeight, setMainHeight] = useState<string>('100vh');
    const headerRef = useRef<HTMLElement | null>(null);
    const [showAddPopup, setShowAddPopup] = useState<boolean>(false);
    const [showEditPopup, setShowEditPopup] = useState<boolean>(false);
    const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
    const [selectedMerchant, setSelectedMerchant] = useState<MerchantResponse | null>(null);
    const [merchants, setMerchants] = useState<MerchantResponse[]>([]);
    const [showConnectionPopup, setShowConnectionPopup] = useState<boolean>(false);
    const [connectionStatus, setConnectionStatus] = useState<{ adapter: boolean; adyen: boolean }>({
        adapter: false,
        adyen: false
    });

    const navigate = useNavigate();
    const handleAddMerchant = () => setShowAddPopup(true);

    useEffect(() => {
        const calculateHeight = () => {
            const headerHeight = headerRef.current?.offsetHeight ?? 0;
            setMainHeight(`calc(100vh - ${headerHeight}px - 64px)`);
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);

        return () => {
            window.removeEventListener('resize', calculateHeight);
        };
    }, []);


    const handleEdit = () => {
        if (selectedMerchant) {
            setShowEditPopup(true);
        } else {
            alert('Please select a row first.');
        }
    };

    const handleDelete = () => {
        if (selectedMerchant) {
            setShowDeletePopup(true);
        } else {
            alert('Please select a row first.');
        }
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleViewConfig = () => {
        if (selectedMerchant) {
            navigate(`/merchant-config/${selectedMerchant.merchantId}`);
        } else {
            alert('Please select a row first.');
        }
    };

    const testConnection = useCallback(async () => {
        if (selectedMerchant) {
            const response = await testAdyenConnection(selectedMerchant.merchantId);
            setConnectionStatus({
                adapter: response.adapterSuccess,
                adyen: response.platformSuccess
            });
            setShowConnectionPopup(true);
        }
    }, [selectedMerchant]);

    const handleViewPaymentAdvice = () => {
        navigate('/payment-advice');
    };

    const handleViewTransactions = () => {
        navigate('/transactions');
    };

    const fetchData = useCallback(async () => {
        const data: MerchantResponse[] = await fetchMerchants();
        if (data) {
            setMerchants(data);
        }
    }, []);

    const handleCloseEdit = async () => {
        await fetchData();
        setShowEditPopup(false);
    };

    return (
        <AuthProvider>
            <HeaderComponent
                ref={(node) => {
                    headerRef.current = node;
                }}
                handleGoHome={handleGoHome}
                handleViewPaymentAdvice={handleViewPaymentAdvice}
                handleViewTransactions={handleViewTransactions}
            />
            <main
                style={{
                    overflow: 'auto',
                    maxHeight: mainHeight,
                    padding: '2rem',
                    height: '100%',
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <AddMerchantAccountComponent
                    showAddPopup={showAddPopup}
                    handleCloseAdd={() => setShowAddPopup(false)}
                    fetchMerchants={fetchData}
                /> {selectedMerchant && (
                <EditMerchantAccountComponent
                    showEditPopup={showEditPopup}
                    selectedMerchant={selectedMerchant}
                    handleCloseEdit={handleCloseEdit}
                    fetchMerchants={fetchData}
                    setSelectedMerchant={setSelectedMerchant}
                />
            )}
                {selectedMerchant && (
                    <DeleteMerchantAccountComponent
                        showDeletePopup={showDeletePopup}
                        selectedMerchant={selectedMerchant}
                        handleCloseDelete={() => setShowDeletePopup(false)}
                        fetchMerchants={fetchData}
                        setSelectedMerchant={setSelectedMerchant}
                    />
                )}
                <Routes>
                    <Route
                        path="/"
                        element={
                            <PrivateRoute>
                                <MerchantListComponent
                                    merchants={merchants}
                                    selectedMerchant={selectedMerchant}
                                    setSelectedMerchant={setSelectedMerchant}
                                    fetchMerchants={fetchData}
                                    onAddMerchant={handleAddMerchant}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onViewConfig={handleViewConfig}
                                    onTestConnection={testConnection}
                                />
                            </PrivateRoute>
                        }
                    />
                    <Route path="/login" element={<LoginComponent/>}/>
                    <Route path="/reset-password/:token" element={<ResetPasswordComponent/>}/>
                    <Route path="/set-password" element={
                        <PrivateRoute>
                            <SetPasswordComponent/>
                        </PrivateRoute>
                    }/>
                    <Route path="/change-email" element={
                        <PrivateRoute>
                            <ChangeEmailComponent/>
                        </PrivateRoute>
                    }/>
                    <Route path="/merchant-config/:merchantId" element={
                        <PrivateRoute>
                            <MerchantConfigurationsListComponent/>
                        </PrivateRoute>
                    }/>
                    {PAYMENT_ADVICE_ENABLED && (<Route path="/payment-advice" element={
                        <PrivateRoute>
                            <PaymentAdviceListComponent/>
                        </PrivateRoute>
                    }/>)}
                    {TRANSACTIONS_ENABLED && (<Route path="/transactions" element={
                        <PrivateRoute>
                            <TransactionsComponent/>
                        </PrivateRoute>
                    }/>)}
                    <Route path="/error" element={<ErrorComponent/>}/>
                </Routes>
            </main>
            <SessionMonitorComponent/>
            <ConnectionStatusPopup
                show={showConnectionPopup}
                adapterStatus={connectionStatus.adapter}
                adyenStatus={connectionStatus.adyen}
                onClose={() => setShowConnectionPopup(false)}
            />
        </AuthProvider>
    );
};

export default App;
